const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-dark-200 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-dark-100 rounded-lg shadow-xl p-8">
          <h1 className="text-4xl font-bold text-white mb-6">Cookie Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: October 24, 2025</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                This Cookie Policy explains how S.C. STUDIO PLATFORMS S.R.L. ("we," "our," or "us") uses cookies and similar tracking technologies on the Studio Blockchain Explorer (the "Service"). This policy should be read in conjunction with our <a href="/privacy-policy" className="text-primary hover:text-primary-light">Privacy Policy</a>.
              </p>
              <p className="mb-4">
                By using our Service, you consent to the use of cookies in accordance with this Cookie Policy. If you do not agree to our use of cookies, you should set your browser settings accordingly or not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. What Are Cookies?</h2>
              <p className="mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p className="mb-4">
                Cookies can be "persistent" or "session" cookies:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Persistent cookies:</strong> Remain on your device for a set period or until you delete them</li>
                <li><strong className="text-white">Session cookies:</strong> Are temporary and are deleted when you close your browser</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Strictly Necessary Cookies</h3>
              <p className="mb-4">
                These cookies are essential for the operation of our Service. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies as the Service would not function properly without them.
              </p>
              <div className="bg-dark-200 p-4 rounded-lg mb-4">
                <p className="mb-2"><strong className="text-white">Purpose:</strong> Authentication, security, load balancing</p>
                <p className="mb-2"><strong className="text-white">Duration:</strong> Session or up to 1 year</p>
                <p className="mb-2"><strong className="text-white">Legal Basis:</strong> Legitimate interest (Article 6(1)(f) GDPR)</p>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">3.2 Performance and Analytics Cookies</h3>
              <p className="mb-4">
                These cookies collect information about how visitors use our Service, such as which pages are visited most often and if users receive error messages. This helps us improve the performance and user experience of our Service.
              </p>
              <div className="bg-dark-200 p-4 rounded-lg mb-4">
                <p className="mb-2"><strong className="text-white">Purpose:</strong> Website analytics, performance monitoring, error tracking</p>
                <p className="mb-2"><strong className="text-white">Duration:</strong> Up to 24 months</p>
                <p className="mb-2"><strong className="text-white">Legal Basis:</strong> Consent (Article 6(1)(a) GDPR)</p>
                <p className="mb-2"><strong className="text-white">Examples:</strong> Google Analytics, custom analytics</p>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">3.3 Functionality Cookies</h3>
              <p className="mb-4">
                These cookies allow our Service to remember choices you make (such as your preferred language or theme) and provide enhanced, personalized features.
              </p>
              <div className="bg-dark-200 p-4 rounded-lg mb-4">
                <p className="mb-2"><strong className="text-white">Purpose:</strong> User preferences, language settings, theme selection</p>
                <p className="mb-2"><strong className="text-white">Duration:</strong> Up to 12 months</p>
                <p className="mb-2"><strong className="text-white">Legal Basis:</strong> Consent (Article 6(1)(a) GDPR)</p>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">3.4 Targeting/Advertising Cookies</h3>
              <p className="mb-4">
                Currently, we do not use targeting or advertising cookies on our Service. If this changes in the future, we will update this Cookie Policy and obtain your consent where required.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Third-Party Cookies</h2>
              <p className="mb-4">
                We may use third-party service providers who set cookies on our Service to provide their services. These third parties have their own privacy and cookie policies:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Google Analytics:</strong> For website analytics and performance monitoring</li>
                <li><strong className="text-white">Content Delivery Networks (CDNs):</strong> For faster content delivery</li>
              </ul>
              <p className="mt-4">
                We do not control these third-party cookies. Please refer to the relevant third party's website for more information about their cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Local Storage and Similar Technologies</h2>
              <p className="mb-4">
                In addition to cookies, we may use other technologies such as:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Local Storage:</strong> To store user preferences and application state</li>
                <li><strong className="text-white">Session Storage:</strong> For temporary data storage during your browsing session</li>
                <li><strong className="text-white">IndexedDB:</strong> For caching blockchain data to improve performance</li>
              </ul>
              <p className="mt-4">
                These technologies serve similar purposes to cookies and are subject to the same consent requirements under applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. How to Manage Cookies</h2>
              <p className="mb-4">
                You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by:
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-3">6.1 Browser Settings</h3>
              <p className="mb-4">
                Most web browsers allow you to control cookies through their settings. You can set your browser to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>Notify you when a cookie is set</li>
              </ul>
              <p className="mb-4">
                Here are links to cookie management instructions for popular browsers:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Microsoft Edge</a></li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.2 Opt-Out Tools</h3>
              <p className="mb-4">
                You can opt out of certain third-party cookies using these tools:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Google Analytics Opt-out Browser Add-on</a></li>
                <li><a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Your Online Choices (EU)</a></li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.3 Impact of Blocking Cookies</h3>
              <p className="mb-4">
                Please note that blocking or deleting cookies may impact your experience on our Service. Some features may not function properly, and you may need to manually adjust certain preferences each time you visit.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Cookies We Use - Detailed List</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-dark-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-white">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-white">Purpose</th>
                      <th className="px-4 py-3 text-left text-white">Type</th>
                      <th className="px-4 py-3 text-left text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    <tr>
                      <td className="px-4 py-3">session_id</td>
                      <td className="px-4 py-3">Maintains user session</td>
                      <td className="px-4 py-3">Strictly Necessary</td>
                      <td className="px-4 py-3">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">theme_preference</td>
                      <td className="px-4 py-3">Stores theme selection</td>
                      <td className="px-4 py-3">Functionality</td>
                      <td className="px-4 py-3">12 months</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">_ga</td>
                      <td className="px-4 py-3">Google Analytics - distinguishes users</td>
                      <td className="px-4 py-3">Analytics</td>
                      <td className="px-4 py-3">24 months</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">_gid</td>
                      <td className="px-4 py-3">Google Analytics - distinguishes users</td>
                      <td className="px-4 py-3">Analytics</td>
                      <td className="px-4 py-3">24 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">cookie_consent</td>
                      <td className="px-4 py-3">Stores cookie consent preferences</td>
                      <td className="px-4 py-3">Strictly Necessary</td>
                      <td className="px-4 py-3">12 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Updates to This Cookie Policy</h2>
              <p className="mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-dark-200 p-4 rounded-lg">
                <p className="mb-2"><strong className="text-white">Email:</strong> office@studio-blockchain.com</p>
                <p className="mb-2"><strong className="text-white">Company:</strong> S.C. STUDIO PLATFORMS S.R.L.</p>
                <p className="mb-2"><strong className="text-white">Address:</strong> Craiova, Romania</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Additional Resources</h2>
              <p className="mb-4">
                For more information about cookies and online privacy, you may find these resources helpful:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">All About Cookies</a></li>
                <li><a href="https://ico.org.uk/for-organisations/guide-to-pecr/cookies-and-similar-technologies/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">ICO Guide to Cookies</a></li>
                <li><a href="https://www.dataprotection.ro/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Romanian Data Protection Authority (ANSPDCP)</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
